import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Navigate, useLocation, matchPath, useParams } from 'react-router-dom'
import { usePantryNavData, usePantryNavLoading } from '@pantry/features'
import Guard from '~/modules/auth/components/Guard'

const PROFILE_PATH = '/s/:storeId/account/my-account'

const ALWAYS_ALLOWED_PATHS = [PROFILE_PATH]

const collectNavPaths = (navData) => {
  const out = []
  if (!navData) return out

  const sections = navData.sections || []
  for (const section of sections) {
    const items = section?.items || []
    for (const item of items) {
      if (!item) continue
      const itemEnd = item?.match?.end ?? false

      if (item.kind === 'route' && item.path) {
        out.push({ path: item.path, end: itemEnd })
        continue
      }

      if (item.kind === 'group') {
        if (item.path) {
          out.push({ path: item.path, end: itemEnd })
        }
        const subItems = item.sub_items || []
        for (const sub of subItems) {
          if (sub?.kind === 'route' && sub?.path) {
            out.push({ path: sub.path, end: sub?.match?.end ?? false })
          }
        }
      }
    }
  }

  return out
}

const getFirstNavPath = (navData) => {
  if (!navData) return null
  const sections = navData.sections || []
  for (const section of sections) {
    const items = section?.items || []
    for (const item of items) {
      if (!item) continue
      if (item.kind === 'route' && item.path) return item.path
      if (item.kind === 'group') {
        if (item.path) return item.path
        const subItems = item.sub_items || []
        for (const sub of subItems) {
          if (sub?.kind === 'route' && sub?.path) return sub.path
        }
      }
    }
  }
  return null
}

const RoleGuard = ({ children }) => {
  const { storeId } = useParams()
  const location = useLocation()
  const navData = usePantryNavData()
  const isNavLoading = usePantryNavLoading()

  const navPaths = useMemo(() => collectNavPaths(navData), [navData])

  const fallbackTo = useMemo(() => {
    const template = getFirstNavPath(navData) || PROFILE_PATH
    return template.replace(':storeId', storeId || '')
  }, [navData, storeId])

  const hasPermissionRole = useCallback(() => {
    const { pathname } = location
    if (!storeId || !matchPath({ path: '/s/:storeId/*' }, pathname)) {
      return true
    }
    if (
      ALWAYS_ALLOWED_PATHS.some(
        (path) => !!matchPath({ path, end: false }, pathname),
      )
    ) {
      return true
    }
    if (isNavLoading && !navData) return true
    return navPaths.some(
      ({ path, end }) => !!matchPath({ path, end }, pathname),
    )
  }, [navPaths, isNavLoading, navData, location, storeId])

  return (
    <Guard
      accessWhen={hasPermissionRole}
      fallbackComp={<Navigate to={fallbackTo} push />}
    >
      {children}
    </Guard>
  )
}

RoleGuard.defaultProps = {}

RoleGuard.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]).isRequired,
}

export default RoleGuard
